import { Alert } from '@creditornot/cb-alert';
import { Button, IconButton, TextButton } from '@creditornot/cb-button';
import { Cross, Plus, Trashbin } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { Radio } from '@creditornot/cb-radio';
import R from 'ramda';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';

import SpecialHoursDateSelect from './SpecialHoursDateSelect';
import { SpecialHoursInputEntry } from './types';
import { DEFAULT_TIME_PAIR, EMPTY_SPECIAL_HOURS_INPUT_ENTRY } from './values';
import TimePairInput from '../TimePairInput';

type Props = {
  className?: string;
  entryErrors?: { [key: string]: string };
  timezone: string;
  value: SpecialHoursInputEntry[];
  onAddEntry?: () => void;
  onChange: (entries: SpecialHoursInputEntry[]) => void;
  onDatePickerModeChange?: (pickerMode: 'single' | 'period') => void;
  onRemoveEntry?: () => void;
};

const Root = styled.div`
  position: relative;
`;

const Entry = styled.div`
  :not(:first-of-type) {
    margin-block-start: 24px;
  }
  :not(:last-of-type) {
    border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  }
`;

const DateSelect = styled(SpecialHoursDateSelect)`
  margin-block-end: 16px;
`;

const NoteInput = styled(Input).attrs({})`
  margin-block-end: 16px;
`;

const RadioButtons = styled.div`
  display: flex;
  margin-block-end: 16px;
  & > * + * {
    margin-inline-start: 16px;
  }
`;

const RadioLabel = styled(T.Body3)``;

const TimePair = styled.div`
  margin-block-end: 16px;
  display: flex;
  align-items: center;
`;

const RemoveIcon = styled(Cross).attrs({})``;

const RemoveTime = styled(IconButton).attrs({
  size: 'small',
  variant: 'grey',
  background: 'staticWhite',
})`
  margin-inline-start: 8px;
`;

const EntryButtons = styled.div`
  margin-block-end: 24px;
  display: flex;
`;

const AddTime = styled(TextButton)`
  margin-inline-end: 12px;
`;

const DeleteIcon = styled(Trashbin)`
  fill: ${cbSemanticCssVars.textSecondary};
`;

const RemoveEntry = styled(TextButton).attrs({ size: 'small', variant: 'grey' })``;

const AddEntry = styled(Button).attrs({ size: 'small', variant: 'lightBlue' })``;

const CautionMessage = styled(Alert).attrs({
  variant: 'error',
  size: 'small',
})`
  margin-block-end: 16px;
`;

const SpecialHoursInput = ({
  className,
  entryErrors = {},
  onAddEntry,
  onChange,
  onDatePickerModeChange,
  onRemoveEntry,
  timezone,
  value: entries,
}: Props) => {
  const intl = useIntl();

  return (
    <Root className={className}>
      {entries.map((entry, entryIndex) => {
        const handleNoteInputChange = (value: string) => {
          onChange(R.update(entryIndex, { ...entry, message: value }, entries));
        };

        return (
          <Entry key={entryIndex}>
            <DateSelect
              periods={entry.periods}
              timezone={timezone}
              onChange={periods => {
                onChange(R.update(entryIndex, { ...entry, periods }, entries));
              }}
              onDateModeChange={onDatePickerModeChange}
            />
            <NoteInput
              value={entry.message}
              autoComplete="off"
              placeholder={intl.formatMessage({ id: 'special-hours.note-placeholder', defaultMessage: 'Leave a note' })}
              onClearClick={() => {
                handleNoteInputChange('');
              }}
              onChange={event => {
                handleNoteInputChange(event.target.value);
              }}
            />
            <RadioButtons>
              <Radio
                checked={!entry.open24Hours && !entry.closedAllDay}
                onChange={event => {
                  onChange(
                    R.update(
                      entryIndex,
                      { ...entry, open24Hours: !event.target.checked, closedAllDay: !event.target.checked },
                      entries,
                    ),
                  );
                }}
              >
                <RadioLabel>
                  <FormattedMessage id="opening-hours.set-hours-manually" defaultMessage="Set hours manually" />
                </RadioLabel>
              </Radio>
              <Radio
                checked={entry.open24Hours}
                onChange={event => {
                  onChange(
                    R.update(
                      entryIndex,
                      { ...entry, open24Hours: event.target.checked, closedAllDay: !event.target.checked },
                      entries,
                    ),
                  );
                }}
              >
                <RadioLabel>
                  <FormattedMessage id="opening-hours.open-24-hours" defaultMessage="Open 24 hours" />
                </RadioLabel>
              </Radio>
              <Radio
                checked={entry.closedAllDay}
                onChange={event => {
                  onChange(
                    R.update(
                      entryIndex,
                      { ...entry, open24Hours: !event.target.checked, closedAllDay: event.target.checked },
                      entries,
                    ),
                  );
                }}
              >
                <RadioLabel>
                  <FormattedMessage id="opening-hours.closed-all-day" defaultMessage="Closed all day" />
                </RadioLabel>
              </Radio>
            </RadioButtons>
            {!entry.open24Hours && !entry.closedAllDay && (
              <>
                {entry.timePairs.map((timePair, timePairIndex) => {
                  return (
                    <TimePair key={timePairIndex}>
                      <TimePairInput
                        value={timePair}
                        onChange={newTimePair => {
                          onChange(
                            R.update(
                              entryIndex,
                              { ...entry, timePairs: R.update(timePairIndex, newTimePair, entry.timePairs) },
                              entries,
                            ),
                          );
                        }}
                      />
                      <RemoveTime
                        onClick={() => {
                          onChange(
                            R.update(
                              entryIndex,
                              { ...entry, timePairs: R.remove(timePairIndex, 1, entry.timePairs) },
                              entries,
                            ),
                          );
                        }}
                      >
                        <RemoveIcon />
                      </RemoveTime>
                    </TimePair>
                  );
                })}
              </>
            )}
            {entryErrors[entryIndex] && <CautionMessage>{entryErrors[entryIndex]}</CautionMessage>}
            <EntryButtons>
              {!entry.open24Hours && !entry.closedAllDay && (
                <AddTime
                  icon={<Plus />}
                  type="button" // Prevent submit if used inside <form>
                  onClick={() => {
                    onChange(
                      R.update(entryIndex, { ...entry, timePairs: [...entry.timePairs, DEFAULT_TIME_PAIR] }, entries),
                    );
                  }}
                >
                  <FormattedMessage id="opening-hours.add-time" defaultMessage="Add time" />
                </AddTime>
              )}
              <RemoveEntry
                icon={<DeleteIcon />}
                type="button" // Prevent submit if used inside <form>
                onClick={() => {
                  onChange(entries.length > 1 ? R.remove(entryIndex, 1, entries) : [EMPTY_SPECIAL_HOURS_INPUT_ENTRY]);
                  if (onRemoveEntry) {
                    onRemoveEntry();
                  }
                }}
              >
                <FormattedMessage id="opening-hours.remove-entry" defaultMessage="Remove entry" />
              </RemoveEntry>
            </EntryButtons>
          </Entry>
        );
      })}
      <AddEntry
        icon={<Plus />}
        type="button" // Prevent submit if used inside <form>
        onClick={() => {
          onChange([...entries, EMPTY_SPECIAL_HOURS_INPUT_ENTRY]);
          if (onAddEntry) {
            onAddEntry();
          }
        }}
      >
        <FormattedMessage id="opening-hours.add-entry" defaultMessage="Add entry" />
      </AddEntry>
    </Root>
  );
};

export default SpecialHoursInput;
